import type { FormConfig, FormMap, PropertySchema } from '@takeshape/schema';
import { assert } from '@takeshape/util';
import type { ImmutableMap, MaybeImmutable } from 'app/common/utils/immutable-types';
// biome-ignore lint/suspicious/noShadowRestrictedNames: Immutable.js Map/Set
import { Map } from 'immutable';
import memoize from 'lodash/memoize';
import React, { createRef, type FormEventHandler, type MutableRefObject, PureComponent } from 'react';
import type { clearForm, registerForm, submitForm } from '../../actions.js';
import type { Data, OnChangeField, OnSetFormData, OnSubmit, OnValidate, ResolveRef, Validator } from '../../types.js';
import SchemaField from '../schema-field.jsx';

const emptyMap = Map();

export const FormContext = React.createContext({});

const defaultClassName = 'flex flex-col gap-6';

export type SchemaFormHookProps<T extends Data> = {
  readonly resolveRef: ResolveRef;
  readonly registerForm: typeof registerForm<T>;
  readonly submitForm: typeof submitForm<T>;
  readonly clearForm: typeof clearForm;
  readonly componentSchema?: ImmutableMap<PropertySchema>;
  readonly schemaPath: string[];
};

type SchemaFormCommonProps<T extends Data> = {
  formName: string;
  onSubmit?: OnSubmit<T>;
  forms?: ImmutableMap<FormMap>;
  form?: string;
  config?: ImmutableMap<FormConfig>;
  context?: Record<string, unknown>;
  widgets?: Record<string, unknown>;
  version?: any;
  disabled?: boolean;
  readOnly?: boolean;
  locale?: string;
  children?: React.ReactNode;
  className?: string;
};

export type SchemaFormPartialOwnProps<T extends Data> = {
  partial: true;
  schemaRef?: string;
  basePath?: string;
} & SchemaFormCommonProps<T>;

export type SchemaFormMainOwnProps<T extends Data> = {
  partial?: false;
  schema: MaybeImmutable<PropertySchema>;
  initialData?: unknown;
  onChangeField?: OnChangeField;
  onSetFormData?: OnSetFormData;
  onValidate?: OnValidate;
  validator?: Validator<T>;
  autoSubmit?: boolean;
  validateOnBlur?: boolean;
} & SchemaFormCommonProps<T>;

export type SchemaFormOwnProps<T extends Data> = SchemaFormMainOwnProps<T> | SchemaFormPartialOwnProps<T>;

export type SchemaFormProps<T extends Data = Data> = SchemaFormOwnProps<T> & SchemaFormHookProps<T>;

const getFormContext = memoize((locale) => ({ locale }));

export default class SchemaForm<T extends Data = Data> extends PureComponent<SchemaFormProps<T>> {
  onChangeFieldRef: MutableRefObject<OnChangeField | undefined>;
  onSetFormDataRef: MutableRefObject<OnSetFormData | undefined>;
  onValidateRef: MutableRefObject<OnValidate | undefined>;
  onSubmitRef: MutableRefObject<OnSubmit<T> | undefined>;
  validatorRef: MutableRefObject<Validator<T> | undefined>;

  constructor(props: SchemaFormProps<T>) {
    super(props);
    this.onChangeFieldRef = createRef() as MutableRefObject<OnChangeField | undefined>;
    this.onSetFormDataRef = createRef() as MutableRefObject<OnSetFormData | undefined>;
    this.onValidateRef = createRef() as MutableRefObject<OnValidate | undefined>;
    this.onSubmitRef = createRef() as MutableRefObject<OnSubmit | undefined>;
    this.validatorRef = createRef() as MutableRefObject<Validator | undefined>;
    this.updateCallbackRefs();
  }

  handleSubmit: FormEventHandler = (e) => {
    e.preventDefault();
    this.props.submitForm(this.props.formName, this.props.onSubmit);
  };

  override componentDidMount() {
    if (!this.props.partial) {
      const { formName, initialData, schema, registerForm, autoSubmit, validateOnBlur } = this.props;
      if (schema) {
        registerForm(formName, schema, initialData, {
          onChangeFieldRef: this.onChangeFieldRef,
          onSetFormDataRef: this.onSetFormDataRef,
          onValidateRef: this.onValidateRef,
          onSubmitRef: this.onSubmitRef,
          validatorRef: this.validatorRef,
          autoSubmit,
          validateOnBlur
        });
      }
    }
  }

  override componentDidUpdate(prevProps: Readonly<SchemaFormProps<T>>) {
    if (!this.props.partial) {
      const { registerForm, version, schema, formName, initialData, autoSubmit, validateOnBlur } = this.props;
      this.updateCallbackRefs();

      assert(!prevProps.partial, 'The value of partial should stay the same throughout the component lifecycle');

      if ((prevProps.version !== version || prevProps.schema !== schema) && (initialData || schema)) {
        registerForm(formName, schema, initialData, {
          onChangeFieldRef: this.onChangeFieldRef,
          onSetFormDataRef: this.onSetFormDataRef,
          onValidateRef: this.onValidateRef,
          onSubmitRef: this.onSubmitRef,
          validatorRef: this.validatorRef,
          autoSubmit,
          validateOnBlur
        });
      }
    }
  }

  override componentWillUnmount() {
    if (!this.props.partial) {
      this.props.clearForm(this.props.formName);
    }
  }

  updateCallbackRefs() {
    if (!this.props.partial) {
      const { onChangeField, onSetFormData, onValidate, onSubmit, validator } = this.props;
      this.onChangeFieldRef.current = onChangeField;
      this.onSetFormDataRef.current = onSetFormData;
      this.onValidateRef.current = onValidate;
      this.onSubmitRef.current = onSubmit;
      this.validatorRef.current = validator;
    }
  }

  override render() {
    const {
      componentSchema,
      formName,
      config,
      forms,
      form,
      context,
      widgets,
      children,
      disabled,
      locale,
      readOnly,
      resolveRef,
      schemaPath,
      className
    } = this.props;

    let formConfig: ImmutableMap<FormConfig> | undefined;
    if (config !== undefined) {
      formConfig = config;
    } else if (form !== undefined && forms !== undefined) {
      formConfig = forms.getIn(form.split('.'));
    }

    let rootField = null;

    const path = this.props.partial ? (this.props.basePath ?? '') : '';
    if (componentSchema) {
      rootField = (
        <FormContext.Provider value={getFormContext(locale)}>
          <SchemaField
            resolveRef={resolveRef}
            schema={componentSchema}
            schemaPath={schemaPath}
            formName={formName}
            path={path}
            config={formConfig || emptyMap}
            forms={forms}
            context={context}
            widgets={widgets}
            disabled={disabled}
            readOnly={readOnly}
            locale={locale}
          />
        </FormContext.Provider>
      );
    }

    if (children) {
      return (
        <form
          data-testid="schema-form"
          onSubmit={this.handleSubmit}
          noValidate
          className={className ?? defaultClassName}
        >
          {rootField}
          {children}
        </form>
      );
    }

    return <div className={className ?? defaultClassName}>{rootField}</div>;
  }
}
